import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchDepartments, fetchRoles, fetchTaskSummary } from "../apis/tasks";
import useAutoRefresh from "../hooks/useAutoRefresh";
import "../styles/components/TasksOverview.css";

const ROLE_ACCENTS = ["blue", "green", "purple", "orange", "teal", "red"];

const toCount = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.departments)) return value.departments;
  return [];
};

const normalizeName = (value) => String(value || "").trim().toLowerCase();

const departmentLabel = (department) =>
  department?.name || department?.department_name || department?.code || "";

const departmentCode = (department) => department?.code || department?.name || "";

const summaryDepartmentLabel = (row) =>
  row?.department_name || row?.department || row?.role_name || row?.name || "";

const firstCount = (row, keys) => {
  const key = keys.find((candidate) => row?.[candidate] !== undefined);
  return key ? toCount(row?.[key]) : 0;
};

const RoleIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="9" cy="8" r="3.2" fill="currentColor" />
    <path d="M3 19c0-3.3 2.7-5.4 6-5.4s6 2.1 6 5.4z" fill="currentColor" />
    <circle cx="17" cy="9.5" r="2.4" fill="currentColor" opacity="0.7" />
    <path
      d="M14 19c0-2.4 1.9-4 4-4s4 1.6 4 4z"
      fill="currentColor"
      opacity="0.7"
    />
  </svg>
);

const OVERVIEW_ITEMS = [
  {
    key: "created_today",
    label: "New Tasks Today",
    tone: "blue",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9" fill="currentColor" opacity="0.15" />
        <path
          d="M12 7.5v9M7.5 12h9"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    key: "completed_today",
    label: "Completed Today",
    tone: "green",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9" fill="currentColor" opacity="0.15" />
        <path
          d="M8 12.4l2.6 2.6L16 9.6"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    key: "total_pending",
    label: "Pending Tasks",
    tone: "orange",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9" fill="currentColor" opacity="0.15" />
        <path
          d="M12 7.5V12l3 1.8"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    key: "total_overdue",
    label: "Overdue Tasks",
    tone: "red",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9" fill="currentColor" opacity="0.15" />
        <path
          d="M12 7.5v5.2"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <circle cx="12" cy="16.2" r="1.2" fill="currentColor" />
      </svg>
    ),
  },
  {
    key: "total_on_hold",
    label: "On Hold Tasks",
    tone: "purple",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9" fill="currentColor" opacity="0.15" />
        <path
          d="M10.2 9v6M13.8 9v6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
];

const TasksOverview = () => {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [summary, setSummary] = useState(null);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    let active = true;

    const resolveAdmin = async () => {
      const cachedRole = localStorage.getItem("role_name");
      if (cachedRole) {
        if (active) setIsAdmin(cachedRole.trim().toLowerCase() === "admin");
        return;
      }

      try {
        const roles = await fetchRoles();
        const roleId = Number(localStorage.getItem("role_id") || 0);
        const list = Array.isArray(roles) ? roles : roles?.roles || [];
        const roleName = list.find((r) => Number(r?.id) === roleId)?.name || "";
        if (roleName) localStorage.setItem("role_name", roleName);
        if (active) setIsAdmin(roleName.trim().toLowerCase() === "admin");
      } catch (error) {
        if (active) setIsAdmin(false);
      }
    };

    resolveAdmin();
    return () => {
      active = false;
    };
  }, []);

  const loadSummary = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const [response, departmentsResponse] = await Promise.all([
        fetchTaskSummary(),
        fetchDepartments(),
      ]);
      if (response?.description || response?.error) return;
      setSummary(response);
      setDepartments(asArray(departmentsResponse));
    } catch (error) {
      // Keep the previously loaded summary on failure.
    }
  }, [isAdmin]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useAutoRefresh(() => loadSummary());

  const openTasks = (departmentId, departmentName = "") =>
    navigate("/tasks", {
      state: {
        view: "all",
        status: "pending",
        departmentId: departmentId || "",
        departmentName: departmentName || "",
      },
    });

  if (!isAdmin || !summary) return null;

  const summaryRows = Array.isArray(summary.departments)
    ? summary.departments
    : Array.isArray(summary.roles)
      ? summary.roles
      : [];
  const departmentRows = departments
    .map((department) => {
      const label = departmentLabel(department);
      const code = departmentCode(department);
      const summaryRow = summaryRows.find((row) => {
        const departmentValues = [label, code].map(normalizeName).filter(Boolean);
        const rowValues = [
          summaryDepartmentLabel(row),
          row?.department_code,
          row?.code,
          row?.role_code,
        ]
          .map(normalizeName)
          .filter(Boolean);
        return departmentValues.some((value) => rowValues.includes(value));
      });
      if (!summaryRow) return null;
      return {
        ...summaryRow,
        id: department.id,
        name: label,
        pending_count: summaryRow.pending_count,
        overdue_count: summaryRow.overdue_count,
      };
    })
    .filter(Boolean);
  const departmentOverviewValue = (key) => {
    const countKeys = {
      created_today: ["created_today", "new_tasks_today"],
      completed_today: ["completed_today"],
      total_pending: ["pending_count", "total_pending", "pending_tasks"],
      total_overdue: ["overdue_count", "total_overdue", "overdue_tasks"],
      total_on_hold: ["on_hold_count", "total_on_hold", "on_hold_tasks"],
    }[key];
    if (!countKeys) return 0;
    return departmentRows.reduce(
      (total, department) => total + firstCount(department, countKeys),
      0,
    );
  };

  return (
    <section className="tasks-overview" aria-label="Tasks Overview">
      <article className="tasks-overview-card department-card">
        <div className="tasks-overview-head">
          <div className="tasks-overview-title-wrap">
            <span className="tasks-overview-title-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <rect
                  x="4"
                  y="3"
                  width="16"
                  height="18"
                  rx="3"
                  fill="none"
                  stroke="#2f6fe0"
                  strokeWidth="1.8"
                />
                <path
                  d="M8 8h8M8 12h8M8 16h5"
                  stroke="#2f6fe0"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <div>
              <h3 className="tasks-overview-title">
                Pending Tasks by Department
              </h3>
              <p className="tasks-overview-subtitle">
                Quick overview of tasks that need attention across departments
              </p>
            </div>
          </div>
          <button
            type="button"
            className="tasks-overview-link"
            onClick={() => openTasks()}
          >
            View All Tasks <span aria-hidden="true">&rarr;</span>
          </button>
        </div>

        {departmentRows.length ? (
          <div className="department-scroller">
            {departmentRows.map((department, index) => {
              const departmentId = department.id;
              const departmentName = department.name;
              const accent = ROLE_ACCENTS[index % ROLE_ACCENTS.length];
              const pending = toCount(department.pending_count);
              const overdue = toCount(department.overdue_count);
              return (
                <div
                  className={`department-tile ${accent}`}
                  key={`${departmentId}-${index}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => openTasks(departmentId, departmentName)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      openTasks(departmentId, departmentName);
                    }
                  }}
                >
                  <span className="department-icon" aria-hidden="true">
                    <RoleIcon />
                  </span>
                  <p className="department-name" title={departmentName || ""}>
                    {String(departmentName || "").trim() || "-"}
                  </p>
                  <p className="department-count">{pending}</p>
                  <p className="department-count-label">Pending Tasks</p>
                  <span className="department-bar" />
                  <p
                    className={`department-overdue ${overdue > 0 ? "alert" : ""}`}
                  >
                    {overdue} Overdue
                  </p>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="tasks-overview-empty">No pending tasks available.</p>
        )}
      </article>

      <article className="tasks-overview-card today-card">
        <div className="tasks-overview-head">
          <div className="tasks-overview-title-wrap">
            <span className="tasks-overview-title-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <rect
                  x="3.5"
                  y="5"
                  width="17"
                  height="15"
                  rx="3"
                  fill="none"
                  stroke="#2f6fe0"
                  strokeWidth="1.8"
                />
                <path
                  d="M3.5 9.5h17M8 3.5v3M16 3.5v3"
                  stroke="#2f6fe0"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <h3 className="tasks-overview-title">Today&apos;s Overview</h3>
          </div>
        </div>

        <ul className="today-list">
          {OVERVIEW_ITEMS.map((item) => (
            <li className="today-item" key={item.key}>
              <span className={`today-icon ${item.tone}`} aria-hidden="true">
                {item.icon}
              </span>
              <span className="today-label">{item.label}</span>
              <span className={`today-value ${item.tone}`}>
                {departmentOverviewValue(item.key)}
              </span>
            </li>
          ))}
        </ul>
      </article>
    </section>
  );
};

export default TasksOverview;
